import { isAdminRole, type ActorRole } from '../types/prioritization/roles';
import { resolvePrimaryJobLabel } from '../types/prioritization/jobRoles';
import { toActorRole } from '../types/prioritization/rolePermissionMapping';
import {
  resolveContributorNavProfile,
  type ContributorNavProfile,
} from './contributorNavProfiles';
import {
  getCachedWorkspaceModulePolicies,
  getCachedWorkspaceModules,
  resolveModuleGate,
} from './workspaceModulesService';

/** Workspace module + feature-flag key for role-based contributor navigation. */
export const CONTRIBUTOR_ROLE_NAV_MODULE_KEY = 'contributor_role_nav';

function isAdminAssignment(roleNames: string[]): boolean {
  return roleNames.some((name) => {
    const actor = toActorRole(name);
    return actor != null && isAdminRole(actor);
  });
}

/**
 * True when the Contributor Role Navigation module+flag gate is open for the
 * current user (build contract §2.7). Safe to call outside React — reads the
 * shared workspace-modules cache (empty cache → catalog defaults).
 */
export function isContributorRoleNavEnabled(isAdmin = false): boolean {
  return resolveModuleGate(
    getCachedWorkspaceModules(),
    getCachedWorkspaceModulePolicies(),
    CONTRIBUTOR_ROLE_NAV_MODULE_KEY,
    isAdmin,
  );
}

/**
 * True when the user should see the contributor navigation shell
 * (MY WORK · OUTPUTS · WORKSPACE) instead of the legacy admin sidebar.
 *
 * Build Contract §1D: focused and operational jobs both use the contributor
 * shell; operational profiles add role-specific links. Only legacy admin roles
 * (platform / tenant / workspace admin) keep the full configuration sidebar.
 *
 * Role logic only — callers must also gate with `isContributorRoleNavEnabled`
 * / `useModuleGate(CONTRIBUTOR_ROLE_NAV_MODULE_KEY)`.
 */
export function shouldUseContributorShell(roleNames: string[]): boolean {
  if (isAdminAssignment(roleNames)) return false;
  return true;
}

/** Resolve shell before DB permissions finish loading (avoids admin-layout flash). */
export function resolveContributorShellState(
  permissionsLoaded: boolean,
  currentRole: ActorRole | null | undefined,
  roleNames: string[],
  roleNavEnabled = true,
): { useContributorShell: boolean; shellReady: boolean } {
  if (!roleNavEnabled) {
    return { useContributorShell: false, shellReady: true };
  }
  if (isActorAdminRole(currentRole) || isAdminAssignment(roleNames)) {
    return { useContributorShell: false, shellReady: true };
  }
  if (permissionsLoaded || roleNames.length > 0) {
    return { useContributorShell: shouldUseContributorShell(roleNames), shellReady: true };
  }
  if (currentRole && !isAdminRole(currentRole)) {
    return { useContributorShell: true, shellReady: true };
  }
  // Permissions still loading — show admin shell rather than blocking the whole app.
  return { useContributorShell: false, shellReady: true };
}

export function getContributorRoleLabel(roleNames: string[]): string {
  return resolvePrimaryJobLabel(roleNames);
}

export function getContributorNavProfile(roleNames: string[]): ContributorNavProfile {
  return resolveContributorNavProfile(roleNames);
}

export function isActorAdminRole(role: ActorRole | string | null | undefined): boolean {
  if (!role) return false;
  const actor = typeof role === 'string' ? toActorRole(role) : role;
  return actor != null && isAdminRole(actor);
}
